/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { Button, Input } from 'antd';
import './styles.less';
import translate from 'i18n/translate';
import { AdmissionFormData } from './types';

interface Props {
  onSubmit?: (values: AdmissionFormData) => void;
  isLoading?: boolean;
}

const AdmissionForm = ({ onSubmit, isLoading }: Props) => {
  const [formData, setFormData] = useState<AdmissionFormData>({
    fullName: '',
    indexNumber: '',
    nicNumber: '',
    district: '',
    contactNumber: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof AdmissionFormData, string>>>({});

  const validateField = (name: keyof AdmissionFormData, value: string) => {
    switch (name) {
      case 'fullName':
        if (!value) return 'Please enter your full name';
        break;
      case 'indexNumber':
        if (!value) return 'Please enter your A/L examination index number';
        if (!/^\d+$/.test(value)) return 'Please enter a valid index number containing only digits';
        break;
      case 'nicNumber':
        if (!value) return 'Please enter your NIC number';
        if (!/^([0-9]{9}[vVxX]|[0-9]{12})$/.test(value)) {
          return 'Please enter a valid Sri Lankan NIC number';
        }
        break;
      case 'district':
        if (!value) return 'Please enter your district';
        break;
      case 'contactNumber':
        if (!value) return 'Please enter your contact number';
        if (!/^(?:07[0-9]{8}|0[1-9][0-9]{8})$/.test(value)) {
          return 'Please enter a valid Sri Lankan phone number (mobile: 07XXXXXXXX, landline: 0XXXXXXXXX)';
        }
        break;
      default:
        break;
    }
    return '';
  };

  const handleChange = (name: keyof AdmissionFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<Record<keyof AdmissionFormData, string>> = {};
    let hasErrors = false;

    (Object.keys(formData) as Array<keyof AdmissionFormData>).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);

    if (!hasErrors && onSubmit) {
      onSubmit(formData);
    }
  };

  return (
    <form className="admission-form" onSubmit={handleSubmit}>
      <div className="form-item">
        <label>{translate('admissionInformation.fullName', true)}</label>
        <Input
          value={formData.fullName}
          onChange={(e) => handleChange('fullName', e.target.value)}
          placeholder={translate('admissionInformation.fullNamePlaceholder', true)}
          className={errors.fullName ? 'ant-input-error' : ''}
        />
        {errors.fullName && <div className="error-message">{errors.fullName}</div>}
      </div>

      <div className="form-item">
        <label>{translate('admissionInformation.indexNumber', true)}</label>
        <Input
          value={formData.indexNumber}
          onChange={(e) => handleChange('indexNumber', e.target.value)}
          placeholder={translate('admissionInformation.indexNumberPlaceHolder', true)}
          className={errors.indexNumber ? 'ant-input-error' : ''}
        />
        {errors.indexNumber && (
          <div className="error-message">{errors.indexNumber}</div>
        )}
      </div>

      <div className="form-item">
        <label>{translate('admissionInformation.nicNumber', true)}</label>
        <Input
          value={formData.nicNumber}
          onChange={(e) => handleChange('nicNumber', e.target.value)}
          placeholder={translate('admissionInformation.nicNumberPlaceholder', true)}
          className={errors.nicNumber ? 'ant-input-error' : ''}
        />
        {errors.nicNumber && <div className="error-message">{errors.nicNumber}</div>}
      </div>

      <div className="form-item">
        <label>{translate('admissionInformation.district', true)}</label>
        <Input
          value={formData.district}
          onChange={(e) => handleChange('district', e.target.value)}
          placeholder={translate('admissionInformation.districtPlaceholder', true)}
          className={errors.district ? 'ant-input-error' : ''}
        />
        {errors.district && <div className="error-message">{errors.district}</div>}
      </div>

      <div className="form-item">
        <label>{translate('admissionInformation.contactNumber', true)}</label>
        <Input
          value={formData.contactNumber}
          onChange={(e) => handleChange('contactNumber', e.target.value)}
          placeholder={translate('admissionInformation.contactNumberPlaceholder', true)}
          className={errors.contactNumber ? 'ant-input-error' : ''}
        />
        {errors.contactNumber && (
          <div className="error-message">{errors.contactNumber}</div>
        )}
      </div>

      <div className="form-item">
        <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
          {translate('admissionInformation.submitBtn', true)}
        </Button>
      </div>
    </form>
  );
};

export default AdmissionForm;
